import * as React from 'react';
import { useCallback } from 'react';

import { VStack } from '@rbilabs/universal-components';
import { LoyaltyTransaction } from 'features/loyalty-transactions/types';

import { t } from 'state/intl/translations';

import { DateAndTimeCell } from './datetime-cell';
import { parseLoyaltyTransaction } from './parseLoyaltyTransactions';
import { PointsLine } from './points-line';
import { PurchaseAmount, Rewards, StyledCurrency } from './transaction.styled';
import { TransactionChannel } from './types';
import { getTransactionMessageId } from './utils';

export const useLocalizedTransactionTransformer = () => {
  const transactionTransformer = useCallback((loyaltyCardTransaction: LoyaltyTransaction) => {
    const {
      totalPayment,
      channel,
      pointsEarned,
      pointsUsed,
      pointsDeducted,
      bonusPointsEarned,
      timeStamp,
      rbiOrderId,
      hasRewards,
    } = parseLoyaltyTransaction(loyaltyCardTransaction);

    const purchaseAmount = (
      <PurchaseAmount>
        <StyledCurrency amount={totalPayment} />
      </PurchaseAmount>
    );

    // the transactions could have points: earned, deducted or used
    // any of those scenarios we display the points message
    const displayPointsMessage = Boolean(
      bonusPointsEarned || pointsEarned || pointsUsed || pointsDeducted || hasRewards
    );
    const pointsMessageId = getTransactionMessageId(channel, pointsDeducted || 0);
    // if there are no points involved in the transaction means
    // that the points were expired or the user earned 0 points
    const noPointsMessage =
      channel === TransactionChannel.PointsExpiration
        ? t('pointsExpired', { points: 0 })
        : t('pointsEarnedLabel', { points: 0 });

    const rewards = (
      <Rewards>
        {displayPointsMessage && (
          <VStack space="$2_5">
            {!!pointsEarned && (
              <PointsLine
                points={pointsEarned}
                description={t(pointsMessageId, { points: pointsEarned })}
              />
            )}
            {!!pointsDeducted && (
              <PointsLine
                points={-pointsDeducted}
                description={t(pointsMessageId, { points: pointsDeducted })}
              />
            )}
            {!!bonusPointsEarned && (
              <PointsLine
                points={bonusPointsEarned}
                description={t('bonusPointsLabel', { points: bonusPointsEarned })}
              />
            )}
            {hasRewards && (
              <PointsLine
                points={-pointsUsed}
                description={t('pointsRedeemed', { points: pointsUsed })}
              />
            )}
          </VStack>
        )}
        {!displayPointsMessage && <PointsLine points={0} description={noPointsMessage} />}
      </Rewards>
    );

    const dateTime = <DateAndTimeCell timeStamp={timeStamp} />;

    return { timeStamp, dateTime, rewards, purchaseAmount, rbiOrderId };
  }, []);

  return transactionTransformer;
};
